<template>
	<transition name="modal">
		<div id="chart-modal" class="modal">
			<div ref="modal-dialog" class="modal-dialog">
				<div id="modal-scroll-element" ref="modalScroll" class="modal-content modal-container">
					<div>
						<button class="button-close" @click="emitClose">X</button>
					</div>
					<span class="share-class-label">Share Class</span>
					<a
						v-for="(shareClass, index) in shareClassList"
						:key="index"
						class="share-class"
						:class="getDividerClass(shareClass, index)"
						@click="setActiveClass"
					>{{ shareClass }}</a>
					<div class="grid">
						<div class="col-left">
							<div
								v-for="(objective, index) in leftColumn"
								:key="index"
								class="category-entry"
								:class="getBorderClass(objective.funds, objective.objective)"
							>
								<div class="category-title">{{ objective.objective }}</div>
								<div v-for="(strategy, index) in objective.funds" :key="index">
									<div v-if="strategy.Objective === 'Retirement Target Date'" class="col-quarter">
										<a :href="strategy.link" class="category-link">{{ strategy.FundNameShort }}</a>
									</div>
									<a v-else :href="strategy.link" class="category-link">
										{{ strategy.FundNameLegal }}
										<sup
											:class="{ 'registered-trademark': strategy.TrademarkLegal === '&reg;' }"
											v-html="strategy.TrademarkLegal"
										/>
									</a>
								</div>
							</div>
						</div>
						<div class="col-right">
							<div
								v-for="(objective, index) in rightColumn"
								:key="index"
								class="category-entry"
								:class="getBorderClass(objective.funds, objective.objective)"
							>
								<div class="category-title">{{ objective.objective }}</div>
								<div v-for="(strategy, index) in objective.funds" :key="index">
									<div v-if="strategy.Objective === 'Retirement Target Date'" class="col-quarter">
										<a :href="strategy.link" class="category-link">{{ strategy.FundNameShort }}</a>
									</div>
									<a v-else :href="strategy.link" class="category-link">
										{{ strategy.FundNameLegal }}
										<sup
											:class="{ 'registered-trademark': strategy.TrademarkLegal === '&reg;' }"
											v-html="strategy.TrademarkLegal"
										/>
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: 'JumpModalShareClass',
	components: {},
	props: ['widgetData'],
	data() {
		return {
			scrollPosition: 0,
			shareClassSelected: ''
		};
	},
	watch: {
		widgetData: {
			immediate: true,
			handler(value) {
				this.shareClassSelected = value.shareClass || '';
			}
		}
	},
	computed: {
		componentData() {
			// just add the urls to widget data
			const formattedData = {};
			if (this.widgetData?.funds) {
				/*eslint-disable prefer-arrow-callback*/
				this.widgetData?.funds.forEach(function(el) {
					if (el && el.ShareClass && el.Objective) {
						el['link'] =
							this.getNonIntegratedLink(el) ||
							this.getIntegratedLink(el);
						//allows the developer to always have access to the integrated link during development only using vue dev tools
						el['integratedLink'] = this.getIntegratedLink(el);

						//created a group of the shareclass
						formattedData[el.ShareClass] =
							formattedData[el.ShareClass] || {};
						//within the shareclass group, create an objective group
						formattedData[el.ShareClass][el.Objective] =
							formattedData[el.ShareClass][el.Objective] || [];
						formattedData[el.ShareClass][el.Objective].push(el);
					}
				}, this);
			}
			//adds objectives categories for each shareclass that were otherwise empty
			for (const shareClass of Object.keys(formattedData)) {
				if (this.widgetData && this.widgetData.totalObjectives) {
					for (const val of this.widgetData.totalObjectives) {
						formattedData[shareClass][val] = formattedData[shareClass][val] || [];
					}
				}
			}
			return formattedData;
		},
		shareClassList() {
			return Object.keys(this.componentData).sort();
		},
		audience() {
			return (this.widgetData && this.widgetData.audience) || 'advisor';
		},
		environment() {
			return (
				(this.widgetData && this.widgetData.environment) || 'production'
			);
		},
		activeClass() {
			const objectives = [];
			const data = this.componentData[this.shareClassSelected] || {};
			for (const objective of Object.keys(data)) {
				objectives.push({
					objective,
					funds: data[objective]
				});
			}
			return objectives;
			// return this.componentData[this.shareClassSelected] || {};
		},
		leftColumn() {
			return this.activeClass.filter((val, index) => index < 5);
		},
		rightColumn() {
			return this.activeClass.filter((val, index) => index >= 5);
		},
		borderClass() {
			return 'border-red';
		},
		objectives() {
			const list = [];
			for (const key in this.componentData) {
				if (Object.prototype.hasOwnProperty.call(this.componentData, key)) {
					list.push(this.componentData[key]);
				}
			}
			return list;
		}
	},
	mounted() {
		this.scrollPosition = window.scrollY;
		document.body.setAttribute('style', 'overflow:hidden;');

		//Sets a max height equal to window height on the scrollable element
		//Makes modal scrollable even on small screens
		const windowHeight = window.innerHeight;
		const modalHeightTarget = this.$refs.modalScroll;
		if (windowHeight && modalHeightTarget) {
			modalHeightTarget.style.maxHeight = `${windowHeight}px`;
		}
		if (this.objectives.length === 1) {
			this.$refs['modal-dialog'].style.setProperty(
				'--modal-width',
				'40%'
			);
			for (const container of this.$refs['category-container']) {
				container.style.setProperty('--category-width', '100%');
			}
		}
	},
	beforeUnmount() {
		document.body.setAttribute('style', '');
		window.scrollTo(0, this.scrollPosition);
	},
	methods: {
		formatGridClassName(className) {
			return className.replace(/\s+/g, '').toLowerCase();
		},
		emitClose() {
			this.$emit('close-modal');
		},
		updatedURL(indentifier) {
			let redirect = window.location.href.split('/');
			redirect.pop();
			redirect.push(indentifier);
			redirect = redirect.join('/');
			return redirect;
		},
		getIntegratedLink(strategy) {
			let link = '';
			// This ensures we have a consistent format for the data received from the client
			if (
				strategy &&
				strategy.URLName &&
				strategy.ShareClass &&
				!this.isMarkitEnvironment
			) {
				const urlName = strategy.URLName.replace(
					`-${strategy.ShareClass.toLowerCase()}.htm`,
					''
				);
				const strategySelector = `${urlName}-${strategy.ShareClass.toLowerCase()}.htm`;
				link = `/${this.audience}/investments/insurance-funds/${strategySelector}`;
			}
			return link;
		},
		getNonIntegratedLink(strategy) {
			if (this.isMarkitEnvironment) {
				return `/${this.audience}/investments/insurance-funds/${strategy.ClassSymbol}`;
			}
			return '';
		},
		setActiveClass(e) {
			//returns the data for the share class that the user is currently viewing
			const target = e.currentTarget;
			if (target) {
				this.shareClassSelected = target.innerText;
			}
		},
		getBorderClass(data, objective) {
			//gets the class that sets the colored border
			//also handles inactive categories
			if (!data.length) {
				return 'inactive';
			}
			//institutional objectives are all dark gray
			if (this.audience === 'institutional') {
				return 'institutional-border';
			}
			//advisor objectives all have unique colors
			const colorLookup = {
				Growth: 'growth-border',
				'Growth and Income': 'growth-and-income-border',
				'Asset Allocation': 'asset-allocation-border',
				'Equity Income': 'equity-income-border',
				Balanced: 'balanced-border',
				Bond: 'bond-border',
				'Portfolio Series': 'portfolio-series-border',
				'Managed Risk': 'portfolio-series-border'
			};
			if (objective) {
				return colorLookup[objective] || 'portfolio-series-border';
			}
		},
		getDividerClass(shareClass, index) {
			//controls whether there should be a divider line or a space for share class grouping
			let dividerClass = 'divider-line';
			if (!index) {
				dividerClass = '';
			}
			if (shareClass === 'P1') {
				dividerClass = 'class-gap';
			}
			if (shareClass === this.shareClassSelected) {
				dividerClass = `${dividerClass} active`;
			}
			return dividerClass;
		}
	}
};
</script>

<style lang="less" scoped>
@import '~_css_/variables.less';
#chart-modal {
	background-color: rgba(0, 0, 0, 0.5);
	transition: opacity 0.3s ease;
	display: block;
	padding-left: 0;
	.modal-dialog {
		--modal-width: 80%;
		--category-width: 50%;
		/*Adds set width for browsers like IE that can't understand the variable width
			It will get overriden if the variable width is valid*/
		width: 1000px;
		width: var(--modal-width);
		max-width: 950px;
		height: calc(100vh - 60px);
		margin: 30px auto;
		.modal-container {
			transition: all 0.3s ease;
			height: 100%;
			margin: auto;
			width: 100%;
			overflow-x: hidden;
			padding: 15px;
			.button-close {
				float: right;
				border: none;
				background-color: #ffffff;
				color: #000000;
				opacity: 0.2;
				font-weight: bold;
				&:hover {
					opacity: 0.4;
				}
			}
			.row {
				margin: 0;
			}
			.share-class-label {
				color: @semi-light-gray;
				font-family: @helvetica-neue;
				margin-right: 10px;
			}
			.share-class {
				font-family: @avenir-demi;
				&.active {
					color: black;
				}
				&.class-gap {
					margin-left: 15px;
				}
			}
			.grid {
				.col-left {
					float: left;
					margin-top: 1.5px;
					width: 49%;
				}
				.col-right {
					float: right;
					width: 49%;
				}
				.col-quarter {
					width: 25%;
					float: left;
				}
				.category-entry {
					margin-top: 30px;
					font-family: @avenir-demi;
					padding-left: 0;
					overflow: auto;
					.category-title {
						color: @dark-gray;
						font-size: 12px;
						margin-bottom: 10px;
						padding-top: 2px;
						text-transform: uppercase;
					}
					.category-link {
						color: @link-color;
						display: block;
						font-size: 14px;
						line-height: 20px;
						margin-bottom: 7px;
						.registered-trademark {
							font-size: 14px;
							top: 0;
							left: 0.1em;
						}
					}
					.category-acronym {
						font-family: @avenir-regular;
						font-size: 12px;
						color: @semi-light-gray;
					}
					&.growth-border {
						border-top: 3px solid #002d72;
					}
					&.growth-and-income-border {
						border-top: 3px solid #0057b8;
					}
					&.asset-allocation-border {
						border-top: 3px solid #6f928a;
					}
					&.equity-income-border {
						border-top: 3px solid #008eaa;
					}
					&.balanced-border {
						border-top: 3px solid #2dccd3;
					}
					&.bond-border {
						border-top: 3px solid #00965e;
					}
					&.portfolio-series-border {
						border-top: 3px solid #7c878e;
					}
					&.institutional-border {
						border-top: 3px solid #888888;
					}
					&.inactive {
						border-top: 3px solid #cccccc;
						.category-title {
							color: #cccccc;
						}
					}
				}
			}
		}
	}
}
.modal-enter {
	opacity: 0;
}

.modal-leave-active {
	opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
	-webkit-transform: scale(1.1);
	transform: scale(1.1);
}
@media (max-width: 768px) {
	#chart-modal .modal-dialog {
		margin: 0;
		height: 100vh;
		width: 100vw;
		max-width: 100vw;
		.modal-content {
			border: none;
			border-radius: initial;
		}
	}
}
</style>